/**
 * FrondEngine: the public facade wiring world, camera, renderer, history,
 * tools and input together around a <canvas>. The app layer talks to this.
 */

import { Camera } from "./camera";
import { WORLD_EXTENT } from "./cell";
import { drawGrid, GridOptions, DEFAULT_GRID } from "./grid";
import { History } from "./history";
import { exportSvg, serializeWorld, deserializeWorld, renderViewport, canvasToBlob } from "./io";
import { Rect, Vec2, inv, rectsIntersect, transformRect } from "./math";
import { Stroke } from "./model";
import { Renderer } from "./renderer";
import { strokeBounds } from "./strokes";
import { elementBoundsInCell, ToolController, PointerState } from "./tools";
import { World } from "./world";

export interface EngineOptions {
  canvas: HTMLCanvasElement;
  background?: string;
  grid?: Partial<GridOptions>;
  initialData?: string;
}

export type EngineEvent =
  | { type: "render" }
  | { type: "camera" }
  | { type: "history" }
  | { type: "tool" }
  | { type: "selection" }
  | { type: "bookmarks" }
  | { type: "hover"; cellId: string; x: number; y: number }
  | { type: "pointer-activity" }
  | { type: "grid" };

type EngineListener = (e: EngineEvent) => void;

export class FrondEngine {
  readonly world: World;
  readonly camera: Camera;
  readonly renderer: Renderer;
  readonly history: History;
  readonly tools: ToolController;
  readonly grid: GridOptions;

  private canvas: HTMLCanvasElement;
  private raf = 0;
  private needRender = true;
  private disposed = false;
  private listeners = new Set<EngineListener>();
  private resizeObserver: ResizeObserver | null = null;
  private lastHover: { cellId: string; x: number; y: number } | null = null;

  constructor(opts: EngineOptions) {
    this.canvas = opts.canvas;
    this.world = new World();
    this.camera = new Camera(this.world.tree);
    this.renderer = new Renderer(opts.canvas, this.world, opts.background ?? "#fdfdfb");
    this.history = new History(this.world);
    this.tools = new ToolController(this.world, this.camera, this.history);
    this.grid = { ...DEFAULT_GRID, ...opts.grid };

    if (opts.initialData) {
      try {
        const parsed = deserializeWorld(opts.initialData);
        this.world.load(parsed.world);
        if (parsed.camera) {
          this.world.tree.ensure(parsed.camera.focusCellId);
          this.camera.focusCellId = parsed.camera.focusCellId;
          this.camera.x = parsed.camera.x;
          this.camera.y = parsed.camera.y;
          this.camera.scale = parsed.camera.scale;
          this.camera.rotation = parsed.camera.rotation;
        }
      } catch {
        // Corrupt initial data: start with a blank world.
      }
    }

    this.world.on("changed", () => this.requestRender());
    this.world.on("bookmarksChanged", () => {
      this.requestRender();
      this.emit({ type: "bookmarks" });
    });
    this.tools.on("bookmark", ({ cellId, x, y, scale }) => {
      this.world.addBookmark(cellId, x, y, scale);
    });
    this.history.on(() => this.emit({ type: "history" }));
    this.tools.on("camera", () => {
      this.requestRender();
      this.emit({ type: "camera" });
    });
    this.tools.on("selection", () => {
      this.requestRender();
      this.emit({ type: "selection" });
    });
    this.tools.on("tool", () => {
      this.requestRender();
      this.emit({ type: "tool" });
    });
    this.tools.on("overlay", () => {
      this.requestRender();
    });

    this.bindInput();
    this.observeSize();
    this.loop();
  }

  /* ------------------------------- events -------------------------------- */

  on(fn: EngineListener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EngineEvent): void {
    for (const fn of this.listeners) fn(e);
  }

  requestRender(): void {
    this.needRender = true;
  }

  /* ----------------------------- render loop ----------------------------- */

  private renderErrorLogged = false;

  private loop = (): void => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    // Smooth the eraser cursor toward the latest pointer position so it glides
    // between pointer events. This returns true only while the cursor is still
    // moving, so a selected-but-idle eraser does not repaint the canvas 60x a
    // second for nothing.
    const cursorMoving = this.tools.tool === "eraser" && this.tools.updateCursorSmoothing();
    if (
      this.needRender ||
      this.world.activeStroke ||
      this.tools.selection.mode !== "none" ||
      cursorMoving
    ) {
      this.needRender = false;
      try {
        this.renderFrame();
        this.emit({ type: "render" });
      } catch (e) {
        // A rendering error must never kill the session: log once and keep
        // the loop alive so the user can still recover (undo, zoom out).
        if (!this.renderErrorLogged) {
          this.renderErrorLogged = true;
          console.error("[frond] render error (continuing):", e);
        }
      }
    }
  };

  renderFrame(): void {
    const { w, h } = this.renderer.size;
    const dpr = this.dpr;
    this.renderer.render(this.camera);
    drawGrid(this.rendererContext(), this.camera, w, h, dpr, this.grid);
    this.renderer.drawCanvasBorder(this.rendererContext(), this.camera, w, h, dpr);
    this.tools.drawOverlay(this.rendererContext(), dpr);
    this.drawHoverPreview();
  }

  private rendererContext(): CanvasRenderingContext2D {
    const ctx = this.canvas.getContext("2d");
    if (!ctx) throw new Error("2D canvas context unavailable");
    return ctx;
  }

  private get dpr(): number {
    return Math.min(window.devicePixelRatio || 1, 2.5);
  }

  /** Apple Pencil hover preview: a small circle under the stylus. */
  private drawHoverPreview(): void {
    const ctx = this.rendererContext();
    const hover = this.lastHover;
    const dpr = this.dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!hover) return;
    const p = this.camera.focusToScreen(
      this.renderer.size.w / 2,
      this.renderer.size.h / 2,
      hover,
    );
    ctx.strokeStyle = "rgba(60,60,60,0.35)";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(2, this.tools.style.size / 2), 0, Math.PI * 2);
    ctx.stroke();
  }

  /* ------------------------------- sizing -------------------------------- */

  private observeSize(): void {
    const parent = this.canvas.parentElement ?? document.body;
    const apply = () => {
      const rect = parent.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        this.renderer.resize(rect.width, rect.height, this.dpr);
        this.tools.canvasSize = { w: rect.width, h: rect.height };
        this.requestRender();
      }
    };
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(apply);
      this.resizeObserver.observe(parent);
    }
    window.addEventListener("resize", apply);
    apply();
  }

  /* -------------------------------- input -------------------------------- */

  private bound: Array<{ target: EventTarget; type: string; fn: EventListenerOrEventListenerObject }> = [];

  private bindInput(): void {
    const canvas = this.canvas;
    canvas.style.touchAction = "none";

    const toPointer = (e: PointerEvent): PointerState => {
      const rect = canvas.getBoundingClientRect();
      return {
        id: e.pointerId,
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
        pointerType: e.pointerType,
        button: e.button,
        pressure: e.pressure > 0 ? e.pressure : 0.5,
        shiftKey: e.shiftKey,
      };
    };

    const updateCursor = () => {
      if (this.tools.isPanning) {
        canvas.style.cursor = "grabbing";
      } else if (this.tools.isPanModifierHeld) {
        canvas.style.cursor = "grab";
      } else {
        canvas.style.cursor = "";
      }
    };

    const add = (target: EventTarget, type: string, fn: EventListenerOrEventListenerObject, opts?: AddEventListenerOptions) => {
      target.addEventListener(type, fn, opts);
      this.bound.push({ target, type, fn });
    };

    add(canvas, "pointerdown", (e) => {
      const pe = e as PointerEvent;
      canvas.setPointerCapture(pe.pointerId);
      this.tools.down(toPointer(pe), pe.ctrlKey || pe.altKey);
      updateCursor();
      this.requestRender();
      this.emit({ type: "pointer-activity" });
    });
    add(canvas, "pointermove", (e) => {
      const pe = e as PointerEvent;
      const p = toPointer(pe);
      this.tools.move(p);
      if (pe.pointerType === "pen" && !this.world.activeStroke && pe.buttons === 0) {
        const focusPt = this.camera.screenToFocus(
          this.renderer.size.w / 2,
          this.renderer.size.h / 2,
          p,
        );
        this.lastHover = { cellId: this.camera.focusCellId, x: focusPt.x, y: focusPt.y };
        this.requestRender();
      }
    });
    add(canvas, "pointerup", (e) => {
      this.tools.up(toPointer(e as PointerEvent));
      updateCursor();
      this.requestRender();
    });
    add(canvas, "pointercancel", (e) => {
      this.tools.cancel(toPointer(e as PointerEvent));
      updateCursor();
      this.requestRender();
    });
    add(canvas, "wheel", (e) => this.tools.wheel(e as WheelEvent), { passive: false });
    add(canvas, "contextmenu", (e) => (e as MouseEvent).preventDefault());

    const isTextInput = (target: EventTarget | null): boolean => {
      const el = target as HTMLElement | null;
      return Boolean(el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable));
    };

    const onKeydown = (e: KeyboardEvent) => {
      if (isTextInput(e.target)) return;
      if (this.tools.keydown(e)) e.preventDefault();
      updateCursor();
    };
    const onKeyup = (e: KeyboardEvent) => {
      if (isTextInput(e.target)) return;
      if (this.tools.keyup(e)) e.preventDefault();
      updateCursor();
    };
    const onBlur = () => {
      this.tools.resetModifiers();
      updateCursor();
    };
    add(window, "keydown", ((e: Event) => onKeydown(e as KeyboardEvent)) as EventListener);
    add(window, "keyup", ((e: Event) => onKeyup(e as KeyboardEvent)) as EventListener);
    add(window, "blur", onBlur as EventListener);
  }

  /* ------------------------------ navigation ----------------------------- */

  fitView(): void {
    const { w, h } = this.renderer.size;
    this.camera.focusCellId = "";
    this.camera.x = 0;
    this.camera.y = 0;
    this.camera.rotation = 0;
    // Fill the viewport with the top-level paper (WORLD_EXTENT half-size plus
    // a small margin). Do NOT normalize: a root-level scale above the
    // [0.5, 2) band is legitimate and keeps the paper framed on screen.
    this.camera.scale = Math.min(w, h) / (WORLD_EXTENT * 2 * 1.1);
    this.requestRender();
    this.emit({ type: "camera" });
  }

  jumpTo(cellId: string, x: number, y: number, targetScale?: number): void {
    this.camera.jumpTo(cellId, x, y, targetScale);
    this.requestRender();
    this.emit({ type: "camera" });
  }

  /**
   * Jump to a bookmark, restoring the magnification it was saved at.
   * `Bookmark.scale` is px per bookmark-cell unit; `Camera.jumpTo` expects
   * the target scale in the *current* frame, so convert (each depth level
   * halves units). Legacy bookmarks without `scale` preserve current zoom.
   */
  jumpToBookmark(b: { cellId: string; x: number; y: number; scale?: number }): void {
    const targetScale =
      b.scale !== undefined
        ? b.scale * Math.pow(2, b.cellId.length - this.camera.focusCellId.length)
        : this.camera.scale;
    this.jumpTo(b.cellId, b.x, b.y, targetScale);
  }

  /** Zoom the view to fit the bounding box of all content. */
  zoomToContent(): void {
    const b = this.contentBounds();
    if (!b) {
      this.fitView();
      return;
    }
    this.camera.focusCellId = b.cellId;
    this.camera.x = (b.minX + b.maxX) / 2;
    this.camera.y = (b.minY + b.maxY) / 2;
    const { w, h } = this.renderer.size;
    const span = Math.max(b.maxX - b.minX, b.maxY - b.minY, 1e-6);
    this.camera.scale = Math.min(w, h) / span / 2.2;
    this.camera.rotation = 0;
    this.camera.normalize("in");
    this.requestRender();
    this.emit({ type: "camera" });
  }

  setGrid(opts: Partial<GridOptions>): void {
    Object.assign(this.grid, opts);
    this.requestRender();
    this.emit({ type: "grid" });
  }

  toggleGrid(): boolean {
    this.grid.enabled = !this.grid.enabled;
    this.requestRender();
    this.emit({ type: "grid" });
    return this.grid.enabled;
  }

  private contentBounds(): { cellId: string; minX: number; minY: number; maxX: number; maxY: number } | null {
    // Use the shallowest cell that contains content, for a stable overview.
    let best: { cellId: string; minX: number; minY: number; maxX: number; maxY: number } | null = null;
    const cells = Object.entries(this.world.allCells());
    for (const [cellId, els] of cells) {
      if (els.length === 0) continue;
      if (best && cellId.length > best.cellId.length) continue;
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      for (const el of els) {
        switch (el.kind) {
          case "stroke": {
            const b = requireStrokeBounds(el);
            minX = Math.min(minX, b.minX);
            minY = Math.min(minY, b.minY);
            maxX = Math.max(maxX, b.maxX);
            maxY = Math.max(maxY, b.maxY);
            break;
          }
          case "shape":
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + el.w);
            maxY = Math.max(maxY, el.y + el.h);
            break;
          case "fill": {
            for (const q of el.points) {
              minX = Math.min(minX, q.x);
              minY = Math.min(minY, q.y);
              maxX = Math.max(maxX, q.x);
              maxY = Math.max(maxY, q.y);
            }
            break;
          }
          case "text": {
            const approxWidth = Math.max(el.fontSize, el.text.length * el.fontSize * 0.6);
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + approxWidth);
            maxY = Math.max(maxY, el.y + el.fontSize);
            break;
          }
          case "image":
            minX = Math.min(minX, el.x);
            minY = Math.min(minY, el.y);
            maxX = Math.max(maxX, el.x + el.w);
            maxY = Math.max(maxY, el.y + el.h);
            break;
        }
      }
      best = { cellId, minX, minY, maxX, maxY };
    }
    return best;
  }

  /* ------------------------------- exports ------------------------------- */

  serialize(): string {
    return serializeWorld(this.world, this.camera);
  }

  /** Serialize the undo/redo stacks so undo survives a browser refresh. */
  serializeHistory(): string {
    return this.history.serialize();
  }

  loadHistory(json: string | null | undefined): void {
    this.history.load(json);
    this.requestRender();
  }

  load(json: string, historyJson?: string | null): void {
    const parsed = deserializeWorld(json);
    this.world.load(parsed.world);
    if (parsed.camera) {
      // The saved camera may point at a cell that holds no content; world
      // loading only recreates content cells, so ensure the focus cell path
      // exists or the render loop would throw every frame.
      this.world.tree.ensure(parsed.camera.focusCellId);
      this.camera.focusCellId = parsed.camera.focusCellId;
      this.camera.x = parsed.camera.x;
      this.camera.y = parsed.camera.y;
      this.camera.scale = parsed.camera.scale;
      this.camera.rotation = parsed.camera.rotation;
    }
    if (historyJson) {
      // Restoring a local session: keep the operation log so the user can
      // undo all the way to the beginning of the drawing.
      this.history.load(historyJson);
    } else {
      this.history.clear();
    }
    // A restored document must never open on an empty view: if the saved
    // camera looks at empty space (the user panned/zoomed away after
    // drawing), bring the content back into view instead.
    if (this.world.elementCount > 0 && !this.hasContentInView()) {
      this.zoomToContent();
    }
    this.requestRender();
  }

  /** True if any element intersects the current viewport. */
  hasContentInView(): boolean {
    const { w, h } = this.renderer.size;
    if (w === 0 || h === 0) return true; // not measured yet — assume visible
    const view = this.viewportRect(w, h);
    let found = false;
    this.world.tree.visit(
      this.camera.focusCellId,
      view,
      () => !found,
      (cell) => {
        if (found) return;
        const els = this.world.elementsInCell(cell.id);
        if (els.length === 0) return;
        const m = this.camera.cellToFocus(cell.id);
        for (const el of els) {
          const b = elementBoundsInCell(el);
          const r = transformRect(m, {
            x: b.minX,
            y: b.minY,
            w: b.maxX - b.minX,
            h: b.maxY - b.minY,
          });
          if (rectsIntersect(r, view)) {
            found = true;
            return;
          }
        }
      },
    );
    return found;
  }

  private viewportRect(w: number, h: number): Rect {
    const m = inv(this.camera.viewTransform(w / 2, h / 2));
    return transformRect(m, { x: 0, y: 0, w, h });
  }

  exportSvg(): string {
    return exportSvg(this.world, "#ffffff");
  }

  exportPng(scaleFactor: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      renderViewport(this.renderer, this.camera, scaleFactor, (canvas) => {
        canvasToBlob(canvas, "image/png").then(resolve, reject);
      });
    });
  }

  /** Preload an image for a newly added image element. */
  preloadImage(src: string): void {
    this.renderer.preloadImage(src);
  }

  dispose(): void {
    this.disposed = true;
    cancelAnimationFrame(this.raf);
    this.resizeObserver?.disconnect();
    // Unbind all DOM listeners so a disposed engine leaves no traces
    // (matters under React StrictMode double-mounting).
    for (const { target, type, fn } of this.bound) {
      target.removeEventListener(type, fn);
    }
    this.bound = [];
  }
}

function requireStrokeBounds(el: Stroke): { minX: number; minY: number; maxX: number; maxY: number } {
  return strokeBounds(el);
}

export type { Vec2 };
